/* ─────────────────────────────────────────────────────────────────────────
   The 3D printer (public/models/zx-printer.glb).

   A real bed-slinger, re-materialled to the site's theme on import: every
   texture — and with it every vendor mark — was dropped in conversion, so
   what arrives is clean white shell / graphite bed / steel Z column.

   It is rigged from its own measured bounds rather than hard-coded numbers:
     Heatbed  slides on Z (the bed throws front to back)
     Motor    is the X gantry, riding up the Z column with the head
     Toolhead rides the gantry: X across, Y with the gantry
   The nozzle runs the enclosure's own print job (printJob in enclosure.js):
   perimeter, raster fill on the floor layers, then up a layer — and the
   plastic appears exactly under it, so scrolling reads as the part being
   built. Origin: centre of the bed's top face, where the enclosure grows.
   ───────────────────────────────────────────────────────────────────────── */
import * as THREE from 'three';
import { ENC, LAYERS, printJob, nozzleFor } from './enclosure';

const BED_W = 1.45;     // how wide the printer's bed reads on stage

/* What the machine reports it is printing. The clock is the job's real
   duration, scaled by progress — the scroll compresses it, the numbers don't. */
export const PRINT_JOB = { file: 'ZX-CORE_ENCLOSURE.gcode', part: 'ZX-Core enclosure', minutes: 134, layers: LAYERS };
export const fmtMinutes = m => (m >= 60 ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`);
export const printRemaining = p => fmtMinutes(Math.ceil((1 - THREE.MathUtils.clamp(p, 0, 1)) * PRINT_JOB.minutes));
/* the lap the head is on: 1..LAYERS while printing */
export function printLayer(p) {
  if (p <= 0) return 0;
  if (p >= 1) return LAYERS;
  return printJob(p).layer + 1;
}

/* The model's gantry rail runs along its own Z, so the machine has to be
   turned a quarter to face the camera: then the head travels in world X and
   the bed throws in world Z, which is what the stage assumes. */
const FACING = -Math.PI / 2;
const box3 = o => new THREE.Box3().setFromObject(o);

/* A cable that actually hangs between two points: the tube is swept once and
   its vertices are rewritten each frame, so no geometry is rebuilt. */
class Cable {
  constructor(radius, material, segs = 56, radial = 7) {
    this.segs = segs; this.radial = radial;
    this.curve = new THREE.CatmullRomCurve3([...Array(5)].map(() => new THREE.Vector3()));
    this.curve.curveType = 'catmullrom'; this.curve.tension = 0.5;
    const verts = (segs + 1) * (radial + 1);
    this.pos = new Float32Array(verts * 3);
    this.nrm = new Float32Array(verts * 3);
    const idx = [];
    for (let i = 0; i < segs; i++) {
      for (let j = 0; j < radial; j++) {
        const a = i * (radial + 1) + j, b = a + radial + 1;
        idx.push(a, b, a + 1, b, b + 1, a + 1);
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(this.pos, 3));
    g.setAttribute('normal', new THREE.BufferAttribute(this.nrm, 3));
    g.setIndex(idx);
    this.mesh = new THREE.Mesh(g, material);
    this.mesh.frustumCulled = false;
    this.r = radius;
    this._t = new THREE.Vector3(); this._n = new THREE.Vector3(); this._b = new THREE.Vector3();
    this._p = new THREE.Vector3(); this._up = new THREE.Vector3(0, 1, 0);
  }

  /* a: fixed end, b: moving end, lift: how high the loop arcs over */
  sweep(a, b, lift) {
    const c = this.curve.points;
    c[0].copy(a);
    c[1].copy(a).lerp(b, 0.22).addScaledVector(this._up, lift * 0.75);
    c[2].copy(a).lerp(b, 0.5).addScaledVector(this._up, lift);
    c[3].copy(a).lerp(b, 0.8).addScaledVector(this._up, lift * 0.62);
    c[4].copy(b);
    const { segs, radial, r } = this;
    for (let i = 0; i <= segs; i++) {
      const t = i / segs;
      this.curve.getPoint(t, this._p);
      this.curve.getTangent(t, this._t).normalize();
      this._n.crossVectors(this._t, this._up);
      if (this._n.lengthSq() < 1e-6) this._n.set(1, 0, 0);
      this._n.normalize();
      this._b.crossVectors(this._t, this._n).normalize();
      for (let j = 0; j <= radial; j++) {
        const th = (j / radial) * Math.PI * 2, cs = Math.cos(th), sn = Math.sin(th);
        const k = (i * (radial + 1) + j) * 3;
        const nx = cs * this._n.x + sn * this._b.x, ny = cs * this._n.y + sn * this._b.y, nz = cs * this._n.z + sn * this._b.z;
        this.pos[k] = this._p.x + nx * r; this.pos[k + 1] = this._p.y + ny * r; this.pos[k + 2] = this._p.z + nz * r;
        this.nrm[k] = nx; this.nrm[k + 1] = ny; this.nrm[k + 2] = nz;
      }
    }
    this.mesh.geometry.attributes.position.needsUpdate = true;
    this.mesh.geometry.attributes.normal.needsUpdate = true;
  }
}

export class Printer {
  constructor(gltf) {
    this.root = new THREE.Group(); this.root.name = 'printer';
    this.root.rotation.y = FACING;
    const model = gltf.scene;
    this.root.add(model);
    this.root.updateMatrixWorld(true);

    this.head = model.getObjectByName('Toolhead');
    const local = o => { const b = box3(o); b.applyMatrix4(new THREE.Matrix4().copy(this.root.matrixWorld).invert()); return b; };

    /* The print surface: the flattest, widest slab under Heatbed — measured in
       the printer's own frame, so the machine's facing can't fool the pick, and
       squareness keeps it from choosing a long thin rail. */
    const bedGroup = model.getObjectByName('Heatbed');
    this.plate = bedGroup.children.reduce((best, o) => {
      const v = local(o).getSize(new THREE.Vector3());
      const square = Math.min(v.x, v.z) / Math.max(v.x, v.z, 1e-6);
      const score = v.x * v.z * square;
      return !best || score > best.score ? { o, score } : best;
    }, null).o;

    /* scale + seat: the plate's top face is the stage's y = 0, its centre the origin */
    const plateSize = local(this.plate).getSize(new THREE.Vector3());
    model.scale.setScalar(BED_W / Math.max(plateSize.x, plateSize.z));
    this.root.updateMatrixWorld(true);
    const seat = local(this.plate);
    const c = seat.getCenter(new THREE.Vector3());
    model.position.sub(new THREE.Vector3(c.x, seat.max.y, c.z));
    this.root.updateMatrixWorld(true);

    /* Rest poses, plus each moving part's parent frame: this is an FBX import,
       so parents carry their own rotation and scale and a stage-space move has
       to be converted before it can be written to `position`. */
    const rootInv = new THREE.Matrix4().copy(this.root.matrixWorld).invert();
    const frame = o => {
      const m = new THREE.Matrix4().multiplyMatrices(rootInv, o.parent.matrixWorld);
      const pos = new THREE.Vector3(), q = new THREE.Quaternion(), sc = new THREE.Vector3();
      m.decompose(pos, q, sc);
      return { q: q.invert(), s: sc };   // printer-local delta → the part's own frame
    };
    this.plateRest = this.plate.position.clone();
    this.headRest = this.head.position.clone();
    this.plateF = frame(this.plate);
    this.headF = frame(this.head);
    /* the gantry beam carries the head up and down the column */
    this.gantry = model.getObjectByName('Motor');
    if (this.gantry) { this.gantryRest = this.gantry.position.clone(); this.gantryF = frame(this.gantry); }
    /* The tip of the real nozzle — not the centre of the whole head, whose
       fan duct pulls that well off to one side of where plastic comes out. */
    let nozzleMesh = null;
    this.head.traverse(o => { if (!nozzleMesh && o.isMesh && /^nozzle/i.test(o.name)) nozzleMesh = o; });
    const hb = local(nozzleMesh || this.head);
    this.nozzle = new THREE.Vector3((hb.min.x + hb.max.x) / 2, hb.min.y, (hb.min.z + hb.max.z) / 2);
    this.bedOffset = new THREE.Vector3();   // world-space throw, Stage rides the part along
    const whole = local(model);
    this.span = { min: whole.min.y, max: whole.max.y };
    /* the machine stands on the floor, so the bed's top ends up this high */
    this.baseLift = -whole.min.y;
    this.bedTop = this.baseLift;
    this.clip = new THREE.Plane(new THREE.Vector3(0, -1, 0), 1e4);
    this.clipped = false;
    this.rootY = 0;

    /* Theme pass: a white machine needs its dark print surface back, or the
       whole thing reads as one white blob against a white studio. */
    const M = (color, roughness, metalness) => new THREE.MeshStandardMaterial({ color, roughness, metalness });
    const theme = {
      Body: M(0x23262c, 0.45, 0.25),       // dark base and shells
      Toolhead: M(0x2b2f36, 0.42, 0.3),    // head housing
      'Z-Axis': M(0x474d57, 0.3, 0.8),     // brushed dark column
      Motor: M(0x393e47, 0.34, 0.7),       // gantry arm and motors
      Heatbed: M(0x0b0c0f, 0.5, 0.4),      // black print surface
    };
    const graphite = M(0x0b0c0f, 0.45, 0.45);
    const accent = M(0xe0070b, 0.34, 0.1);
    this.mats = [graphite, accent, ...Object.values(theme)];
    model.traverse(o => {
      if (!o.isMesh) return;
      o.castShadow = true; o.receiveShadow = true;
      o.material = theme[o.material.name] || theme.Body;
    });
    this.plate.traverse(o => { if (o.isMesh) o.material = graphite; });
    /* a red ring at the hot end, the one warm detail on a cool machine */
    const collar = new THREE.Mesh(new THREE.TorusGeometry(0.022, 0.006, 10, 28), accent);
    collar.rotation.x = Math.PI / 2;
    collar.position.copy(this.nozzle).add(new THREE.Vector3(0, 0.035, 0));
    this.root.add(collar);
    this.head.attach(collar);   // keep its place, but ride with the head


    /* The model's cable loop is a frozen mesh, so it is replaced with cables
       that are re-swept each frame between the frame and the moving head. */
    const rubber = M(0x0a0b0d, 0.85, 0.05);
    this.mats.push(rubber);
    const frozen = [];
    model.traverse(o => { if (o.isMesh && /^(CableTrans|CableSOlid)/i.test(o.name)) frozen.push(o); });
    frozen.forEach(o => { o.visible = false; });
    /* the loop leaves the top of the Z column and lands on the head's port */
    const col = local(model.getObjectByName('Z_Rail') || model.getObjectByName('Z_Axis'));
    this.cableAnchor = new THREE.Vector3((col.min.x + col.max.x) / 2, col.max.y - 0.12, (col.min.z + col.max.z) / 2);
    const hd = local(this.head);
    /* Land the cable on the head's actual shell, not on its bounding box: fire a
       ray at the head from the column side and take the first surface hit, then
       sink the end a little way in so the tube visibly enters the housing. */
    const mid = new THREE.Vector3((hd.min.x + hd.max.x) / 2, hd.max.y - 0.07, (hd.min.z + hd.max.z) / 2);
    const side = this.cableAnchor.z < mid.z ? -1 : 1;
    const from = mid.clone().setZ(side < 0 ? hd.min.z - 0.25 : hd.max.z + 0.25).setY(hd.max.y + 0.12);
    const dir = mid.clone().sub(from).normalize();
    const rc = new THREE.Raycaster(
      from.clone().applyMatrix4(this.root.matrixWorld),
      dir.clone().transformDirection(this.root.matrixWorld).normalize(),
    );
    const hit = rc.intersectObject(this.head, true)[0];
    this.cableHead = hit
      ? hit.point.clone().applyMatrix4(rootInv).addScaledVector(dir, 0.03)
      : mid.clone().setZ(side < 0 ? hd.min.z + 0.02 : hd.max.z - 0.02);
    this.cables = [new Cable(0.018, rubber), new Cable(0.011, rubber)];
    this.cables.forEach((c, i) => { c.offset = i === 0 ? 0 : 0.045; this.root.add(c.mesh); });

    /* ── the machine's own screen, running Zone-X ───────────────────────── */
    const disp = model.getObjectByName('Display');
    if (disp) {
      const d = local(disp);
      const c2 = new THREE.Vector3((d.min.x + d.max.x) / 2, (d.min.y + d.max.y) / 2, (d.min.z + d.max.z) / 2);
      const eye = c2.clone().add(new THREE.Vector3(0.5, 0.35, 0));          // in front and above
      const ray = c2.clone().sub(eye).normalize();
      const rcD = new THREE.Raycaster(
        eye.clone().applyMatrix4(this.root.matrixWorld),
        ray.clone().transformDirection(this.root.matrixWorld).normalize(),
      );
      const face = rcD.intersectObject(disp, true)[0];
      this.screenCanvas = document.createElement('canvas');
      this.screenCanvas.width = 640; this.screenCanvas.height = 420;
      this.screenCtx = this.screenCanvas.getContext('2d');
      this.screenTex = new THREE.CanvasTexture(this.screenCanvas);
      this.screenTex.colorSpace = THREE.SRGBColorSpace;
      this.screenTex.anisotropy = 4;
      const screenMat = new THREE.MeshBasicMaterial({ map: this.screenTex, toneMapped: false });
      this.mats.push(screenMat);
      this.screen = new THREE.Mesh(new THREE.PlaneGeometry(0.2, 0.131), screenMat);
      if (face) {
        const pLocal = face.point.clone().applyMatrix4(rootInv);
        const nLocal = face.face.normal.clone().transformDirection(face.object.matrixWorld).transformDirection(rootInv).normalize();
        this.screen.position.copy(pLocal).addScaledVector(nLocal, 0.004);
        this.screen.lookAt(this.screen.position.clone().add(nLocal));
      } else {
        this.screen.position.set(d.max.x + 0.004, c2.y, c2.z);
        this.screen.rotation.y = Math.PI / 2;
      }
      this.root.add(this.screen);
      this.drawScreen(0);
    }

    /* the hot end: a molten bead at the tip, and a warm glow on the fresh
       plastic around it (the light is dimmed, never removed, so toggling it
       doesn't recompile every material in the scene) */
    this.tip = new THREE.Mesh(new THREE.SphereGeometry(0.007, 12, 12), new THREE.MeshBasicMaterial({ color: 0xff5a1a, toneMapped: false }));
    this.root.add(this.tip);
    this.mats.push(this.tip.material);
    this.glow = new THREE.PointLight(0xff6a2a, 0, 0.35, 2);
    this.glow.position.y = 0.02;
    this.tip.add(this.glow);
  }

  /* The panel on the base: Zone-X branding plus what the machine is doing. */
  drawScreen(p) {
    const c = this.screenCtx; if (!c) return;
    const W = 640, H = 420;
    const pct = Math.round(THREE.MathUtils.clamp(p, 0, 1) * 100);
    const done = p >= 1, idle = p <= 0;
    c.fillStyle = '#0a0b0e'; c.fillRect(0, 0, W, H);
    c.fillStyle = 'rgba(255,255,255,0.05)'; c.fillRect(0, 0, W, 74);
    c.fillStyle = '#ff0004'; c.fillRect(0, 0, 6, 74);

    c.textAlign = 'left';
    c.font = '800 38px Inter, system-ui, sans-serif';
    c.fillStyle = '#ffffff'; c.fillText('ZONE', 30, 50);
    const w = c.measureText('ZONE').width;
    c.fillStyle = '#ff2b2e'; c.fillText('-X', 30 + w, 50);
    c.font = '500 13px "JetBrains Mono", monospace';
    c.fillStyle = 'rgba(255,255,255,0.45)';
    c.fillText('ADDITIVE  ·  A1', W - 178, 46);

    /* the job on the bed: the housing the board is about to be built into */
    c.fillStyle = 'rgba(255,255,255,0.8)';
    c.font = '600 15px "JetBrains Mono", monospace';
    c.fillText(PRINT_JOB.file, 30, 112);
    c.fillStyle = 'rgba(255,255,255,0.5)';
    c.font = '500 14px "JetBrains Mono", monospace';
    const job = printJob(p);
    const stage = job.floor ? 'BASE + WALL' : 'WALLS';
    c.fillText(idle ? 'READY' : done ? 'COMPLETE' : `PRINTING  ·  ${stage}`, 30, 140);

    c.textAlign = 'left';
    c.fillStyle = '#ffffff'; c.font = '800 96px Inter, system-ui, sans-serif';
    c.fillText(String(pct), 26, 240);
    const nw = c.measureText(String(pct)).width;
    c.fillStyle = 'rgba(255,255,255,0.55)'; c.font = '600 30px Inter, sans-serif';
    c.fillText('%', 32 + nw, 240);
    c.textAlign = 'right';
    c.fillStyle = 'rgba(255,255,255,0.4)'; c.font = '500 13px "JetBrains Mono", monospace';
    c.fillText(done ? 'PRINT TIME' : 'REMAINING', W - 30, 206);
    c.fillStyle = '#ffffff'; c.font = '600 27px Inter, sans-serif';
    c.fillText(done ? fmtMinutes(PRINT_JOB.minutes) : printRemaining(p), W - 30, 240);
    c.textAlign = 'left';

    c.fillStyle = 'rgba(255,255,255,0.12)'; c.fillRect(30, 268, W - 60, 8);
    c.fillStyle = done ? '#19e36b' : '#ff0004'; c.fillRect(30, 268, (W - 60) * (pct / 100), 8);

    const cells = [['LAYER', `${printLayer(p)}/${PRINT_JOB.layers}`],
                   ['NOZZLE', '210°C'], ['BED', '60°C']];
    cells.forEach(([k, val], i) => {
      const x = 30 + i * 196;
      c.fillStyle = 'rgba(255,255,255,0.4)'; c.font = '500 13px "JetBrains Mono", monospace';
      c.fillText(k, x, 330);
      c.fillStyle = '#ffffff'; c.font = '600 27px Inter, sans-serif';
      c.fillText(val, x, 366);
    });
    this.screenTex.needsUpdate = true;
  }

  /* where the nozzle should be at print progress p (0..1), in stage space */
  nozzleAt(p) {
    return nozzleFor(printJob(p));
  }

  /* p: print progress, park: 0..1 the head stepping aside once finished.

     The gantry rail runs along the model's own Z, so the head travels in local
     Z and up, and the bed supplies local X. For a target point on the part:
       head.z = qLocal.z - nozzle.z,  head.y = qLocal.y - nozzle.y,
       bed.x  = nozzle.x - qLocal.x
     with qLocal the target expressed in the printer's own frame — which holds
     whichever way the machine is turned to face the camera. */
  update(p, park, time, approach = 1) {
    const printing = p > 0 && p < 1;
    const target = this.nozzleAt(THREE.MathUtils.clamp(p, 0, 0.9999));
    const parked = new THREE.Vector3(ENC.W * 0.8, ENC.H + 0.4, -ENC.D * 0.6);
    /* approach: the travel move from parked down to the first layer's start,
       so the head arrives where the plastic begins instead of jumping there */
    const a = approach * approach * (3 - 2 * approach);
    const at = p <= 0 ? parked.clone().lerp(target, a) : printing ? target : target.clone().lerp(parked, park);
    const home = p >= 1 ? park : 0;   // the bed comes back square once the part is done

    const inv = this.root.quaternion.clone().invert();
    const qL = at.clone().applyQuaternion(inv);
    const n = this.nozzle;
    const dx = (1 - home) * (n.x - qL.x);
    this.bedOffset.set(dx, 0, 0).applyQuaternion(this.root.quaternion);

    const toLocal = (f, v) => v.applyQuaternion(f.q).divide(f.s);
    this.plate.position.copy(this.plateRest).add(toLocal(this.plateF, new THREE.Vector3(dx, 0, 0)));
    this.head.position.copy(this.headRest).add(toLocal(this.headF, new THREE.Vector3(0, qL.y - n.y, qL.z - n.z)));
    if (this.gantry) this.gantry.position.copy(this.gantryRest).add(toLocal(this.gantryF, new THREE.Vector3(0, qL.y - n.y, 0)));

    /* cables follow the head: the loop stretches and its arc flattens as the
       head runs away from the anchor, and rides up with the gantry */
    const headNow = this.cableHead.clone().add(new THREE.Vector3(0, qL.y - n.y, qL.z - n.z));
    const span = headNow.distanceTo(this.cableAnchor);
    this.cables.forEach(c => {
      const a = this.cableAnchor.clone().add(new THREE.Vector3(c.offset, 0, 0));
      const b = headNow.clone().add(new THREE.Vector3(c.offset, -c.offset * 0.4, 0));
      c.sweep(a, b, THREE.MathUtils.clamp(0.26 - span * 0.1, 0.05, 0.26));
    });

    this.tip.position.copy(qL).add(new THREE.Vector3(dx, 0, 0));
    if ((this._scr = (this._scr || 0) + 1) % 8 === 0 || this._lastP === undefined || Math.abs((this._lastP || 0) - p) > 0.02) {
      this._lastP = p; this.drawScreen(p);
    }
    this.tip.visible = printing;
    this.tip.scale.setScalar(printing ? 1 + Math.sin(time * 9) * 0.12 : 1);
    this.glow.intensity = printing ? 0.05 * (0.9 + Math.sin(time * 7) * 0.1) : 0;
  }

  /* The machine builds itself up from the bed and sinks back the same way —
     a fade leaves a dark ghost of the base hanging over a white studio. */
  setReveal(k) {
    const on = k > 0.005;
    this.root.visible = on;
    if (!on) return;
    const span = this.span;
    /* The clip stays attached for good and is only moved out of the way once
       the machine is whole: adding or removing a clipping plane recompiles
       every material on the printer, a visible stall right as printing starts. */
    if (!this.clipped) { this.mats.forEach(m => { m.clippingPlanes = [this.clip]; m.clipShadows = true; m.needsUpdate = true; }); this.clipped = true; }
    if (k >= 0.999) {
      this.root.position.y = this.baseLift;
      this.clip.set(new THREE.Vector3(0, -1, 0), 1e4);
      return;
    }
    const e = k * k * (3 - 2 * k);
    this.root.position.y = this.baseLift + (e - 1) * 0.12;       // a small settle as it arrives
    const h = span.min + (span.max - span.min + 0.06) * e;
    this.clip.set(new THREE.Vector3(0, -1, 0), h + this.root.position.y);
  }
}
